import { z } from 'zod';
/**
 * Einstellungen · Organisation „Rechenannahmen Selbstauskunft" (D-156, docs/dev/api-organization.md „Calculation
 * assumptions (settings)"): the organization-wide assumptions of the volume calculation (spec §14). The request
 * schema is shared by the API (validation) and the SPA (inline messages); the response types describe what the API
 * sends. Ranges mirror the table CHECK `calculation_assumptions_range` and the column types (`bigint` cents,
 * `numeric(6,3)` percentages).
 */
const threeDecimals = (value) => Math.abs(Math.round(value * 1000) - value * 1000) < 1e-6;
const THREE_DECIMALS_MESSAGE = 'Höchstens drei Nachkommastellen.';
const NUMBER_MESSAGE = 'Bitte tragen Sie eine Zahl ein.';
/** Upper bound of `numeric(6,3)`. */
const PCT_COLUMN_MAX = 999.999;
const shareOfHundred = (rangeMessage) => z
    .number({ message: NUMBER_MESSAGE })
    .min(0, { message: rangeMessage })
    .max(100, { message: rangeMessage })
    .refine(threeDecimals, { message: THREE_DECIMALS_MESSAGE });
const annualRate = (negativeMessage) => z
    .number({ message: NUMBER_MESSAGE })
    .min(0, { message: negativeMessage })
    .max(PCT_COLUMN_MAX, { message: 'Höchstens 999,999 %.' })
    .refine(threeDecimals, { message: THREE_DECIMALS_MESSAGE });
const rateUpTo = (max, rangeMessage) => z
    .number({ message: NUMBER_MESSAGE })
    .min(0, { message: rangeMessage })
    .max(max, { message: rangeMessage })
    .refine(threeDecimals, { message: THREE_DECIMALS_MESSAGE });
const wholeNumber = (min, max, rangeMessage) => z
    .number({ message: NUMBER_MESSAGE })
    .int({ message: rangeMessage })
    .min(min, { message: rangeMessage })
    .max(max, { message: rangeMessage });
const amountPerSqm = (negativeMessage) => z
    .number({ message: 'Bitte tragen Sie einen Betrag ein.' })
    .int({ message: 'Bitte tragen Sie einen Betrag in Euro und Cent ein.' })
    .min(0, { message: negativeMessage })
    .max(Number.MAX_SAFE_INTEGER, { message: 'Der Betrag ist zu groß.' });
export const ANNUITY_ZERO_MESSAGE = 'Kalkulationszins und Tilgung zusammen müssen über 0 % liegen.';
export const calculationAssumptionsBodySchema = z
    .object({
    livingExpensesCents: z
        .number({ message: 'Bitte tragen Sie einen Betrag ein.' })
        .int({ message: 'Bitte tragen Sie einen Betrag in Euro und Cent ein.' })
        .min(0, { message: 'Die Lebenshaltungspauschale darf nicht negativ sein.' })
        .max(Number.MAX_SAFE_INTEGER, { message: 'Der Betrag ist zu groß.' }),
    rentCreditPct: shareOfHundred('Die Anrechnung muss zwischen 0 und 100 % liegen.'),
    capPct: shareOfHundred('Die Kappungsgrenze muss zwischen 0 und 100 % liegen.'),
    interestPct: annualRate('Der Kalkulationszins darf nicht negativ sein.'),
    repaymentPct: annualRate('Die Tilgung darf nicht negativ sein.'),
    // Section „Immobilienrechner" (owner decision 19.10.2026): the investment calculator's defaults.
    calcInterestPct: annualRate('Der Sollzins darf nicht negativ sein.'),
    calcRepaymentPct: annualRate('Die Anfangstilgung darf nicht negativ sein.'),
    calcFixedInterestYears: wholeNumber(1, 30, 'Die Zinsbindung muss zwischen 1 und 30 Jahren liegen.'),
    calcFollowUpInterestPct: annualRate('Der Anschlusszins darf nicht negativ sein.'),
    calcFollowUpRepaymentPct: annualRate('Die Anschlusstilgung darf nicht negativ sein.'),
    calcRentIncreasePct: shareOfHundred('Die Mietsteigerung muss zwischen 0 und 100 % liegen.'),
    calcRentIncreaseIntervalMonths: wholeNumber(0, 360, 'Das Intervall muss zwischen 0 und 360 Monaten liegen.'),
    calcAppreciationPct: shareOfHundred('Die Wertentwicklung muss zwischen 0 und 100 % liegen.'),
    calcDepreciationPct: rateUpTo(10, 'Die AfA muss zwischen 0 und 10 % liegen.'),
    calcTaxRatePct: rateUpTo(60, 'Der Steuersatz muss zwischen 0 und 60 % liegen.'),
    calcReservePerSqmCents: amountPerSqm('Die Instandhaltungsrücklage darf nicht negativ sein.'),
    calcOtherCostsPerSqmCents: amountPerSqm('Die sonstigen Kosten dürfen nicht negativ sein.'),
})
    // negative values carry their own message; this one is for both at 0 (no annuity, every volume would be 0)
    .refine((body) => body.interestPct !== 0 || body.repaymentPct !== 0, {
    path: ['repaymentPct'],
    message: ANNUITY_ZERO_MESSAGE,
});
/**
 * The sample household of the example line („Beispiel: … Finanzierungsvolumen bei 3.500 € Nettoeinkommen und
 * 1.100 € Warmmiete"): the server computes it with the assumptions in force, the SPA recomputes it live while the
 * owner edits — both with `calculateFinancingVolume`.
 */
export const CALCULATION_ASSUMPTIONS_EXAMPLE_INPUT = {
    netIncomeCents: 350_000,
    otherIncomeCents: 0,
    rentalIncomeCents: 0,
    grossRentCents: 110_000,
    leasingCents: 0,
    loansCents: 0,
    alimonyCents: 0,
};
//# sourceMappingURL=calculation-assumptions.js.map